import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, vShow as _vShow, withDirectives as _withDirectives, resolveComponent as _resolveComponent, withCtx as _withCtx, unref as _unref, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, normalizeClass as _normalizeClass, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "vc-incoming-hero" }
const _hoisted_2 = { class: "vc-incoming-hero__title text-wrap" }
const _hoisted_3 = { class: "vc-incoming-hero__purpose text-wrap" }
const _hoisted_4 = {
  key: 0,
  class: "vc-rail__avatar-wrap",
  "aria-hidden": "true"
}
const _hoisted_5 = { class: "vc-rail-cta-stack mt-auto pt-2" }

import { computed, onMounted, onBeforeUnmount, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useCallStore } from '../stores/callStore'
import { signalingService } from '../services/signalingService'
import { apiClient } from '../services/apiClient'
import PatientBackground from '../components/PatientBackground.vue'
import type { SessionCredentials } from '@virtual-care/shared'
import { incomingCallHeadline, incomingPurposeLine } from '@virtual-care/shared'
import { useNurseAvatarInitials } from '../composables/useNurseAvatarInitials'

interface Props {
  sessionId: string
}


export default /*@__PURE__*/_defineComponent({
  __name: 'IncomingCallView',
  props: {
    sessionId: {}
  },
  setup(__props: any) {

const props = __props
const router = useRouter()
const callStore = useCallStore()
const mounted = ref(false)

const resolvedSessionId = computed(() => callStore.sessionId ?? props.sessionId)

const nurseNameForAvatar = computed(() => callStore.nurseName)
const avatarInitials = useNurseAvatarInitials(nurseNameForAvatar)

/** Figma: avatar only when showing a specific nurse (not generic headline). */
const showNurseAvatar = computed(
  () => !callStore.useGenericNurseLabel && !!callStore.nurseName?.trim()
)

const nurseCallingLine = computed(() =>
  incomingCallHeadline({
    nurseName: callStore.nurseName,
    useGenericNurseLabel: callStore.useGenericNurseLabel,
  })
)

const callPurpose = computed(() => incomingPurposeLine(callStore.callPurpose))

const handleAccept = async () => {
  const sessionId = resolvedSessionId.value
  const roomId = apiClient.getRoomId()

  try {
    const joinResponse = await apiClient.joinSession(sessionId, 'patient')

    console.log('[IncomingCallView] Joined session', {
      provider: joinResponse.session?.provider,
      hasCredentials: !!joinResponse.session?.credentials
    })

    const provider = joinResponse.session?.provider
    if (!provider) {
      throw new Error('No video provider returned from backend')
    }
    
    const credentials = joinResponse.session?.credentials

    callStore.acceptCall(
      provider as never,
      credentials as SessionCredentials | undefined
    )

    signalingService.notifyCallAccepted(sessionId, roomId)

    callStore.setStatus('connecting')
    router.push(`/connecting/${sessionId}`)
  } catch (err) {
    console.error('[IncomingCallView] Failed to accept call:', err)
    callStore.endCall()
    router.push('/')
  }
}

const handleDecline = async () => {
  const sessionId = resolvedSessionId.value
  try { await apiClient.declineSession(sessionId) } catch { /* best-effort */ }
  callStore.endCall()
  router.push('/decline-confirm')
}

const handleCallEnded = async () => {
  console.log('[IncomingCallView] handleCallEnded called, sessionId:', resolvedSessionId.value)
  const sessionId = resolvedSessionId.value
  
  // Mark session as missed and update store state
  try { await apiClient.markSessionMissed(sessionId) } catch (err) { 
    console.warn('[IncomingCallView] markSessionMissed failed:', err)
  }
  
  // Set status to missed to persist state
  callStore.setStatus('missed')
  
  console.log('[IncomingCallView] Routing to /missed/', sessionId)
  // Pass sessionId to missed view so it can access session data even if store is cleared
  router.push(`/missed/${sessionId}`)
}

onMounted(() => {
  mounted.value = true
  signalingService.on('call_ended', handleCallEnded)
})

onBeforeUnmount(() => {
  signalingService.off('call_ended', handleCallEnded)
})

return (_ctx: any,_cache: any) => {
  const _component_v_fade_transition = _resolveComponent("v-fade-transition")!
  const _component_v_avatar = _resolveComponent("v-avatar")!
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_VBtnPrimary = _resolveComponent("VBtnPrimary")!
  const _component_VBtnTextDark = _resolveComponent("VBtnTextDark")!
  const _component_v_sheet = _resolveComponent("v-sheet")!
  const _component_v_container = _resolveComponent("v-container")!

  return (_openBlock(), _createBlock(_component_v_container, {
    fluid: "",
    class: "pa-0 fill-height bg-backgroundBase vc-incoming-root"
  }, {
    default: _withCtx(() => [
      _createVNode(PatientBackground),
      _createVNode(_component_v_fade_transition, { appear: "" }, {
        default: _withCtx(() => [
          _withDirectives(_createElementVNode("div", _hoisted_1, [
            _createElementVNode("h1", _hoisted_2, _toDisplayString(nurseCallingLine.value), 1),
            _createElementVNode("p", _hoisted_3, _toDisplayString(callPurpose.value), 1)
          ], 512), [
            [_vShow, mounted.value]
          ])
        ]),
        _: 1
      }),
      _createVNode(_component_v_fade_transition, { appear: "" }, {
        default: _withCtx(() => [
          _withDirectives(_createVNode(_component_v_sheet, {
            class: _normalizeClass(["d-flex flex-column bg-white vc-incoming-panel", { 'vc-incoming-panel--with-avatar': showNurseAvatar.value }]),
            elevation: "24",
            height: "100%",
            rounded: "0"
          }, {
            default: _withCtx(() => [
              (showNurseAvatar.value)
                ? (_openBlock(), _createElementBlock("div", _hoisted_4, [
                    _createVNode(_component_v_avatar, {
                      class: "vc-rail__avatar",
                      size: "120"
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_unref(avatarInitials)), 1)
                      ]),
                      _: 1
                    })
                  ]))
                : _createCommentVNode("", true),
              _createElementVNode("div", {
                class: _normalizeClass(showNurseAvatar.value ? 'mb-10' : 'mb-16')
              }, [
                _createVNode(_component_v_icon, {
                  class: "vc-rail__video-icon",
                  color: "primary"
                }, {
                  default: _withCtx(() => [...(_cache[0] || (_cache[0] = [
                    _createTextVNode("mdi-video-plus-outline", -1)
                  ]))]),
                  _: 1
                })
              ], 2),
              _cache[5] || (_cache[5] = _createElementVNode("div", {
                class: "flex-grow-1",
                "aria-hidden": "true"
              }, null, -1)),
              _createElementVNode("div", _hoisted_5, [
                _createVNode(_component_VBtnPrimary, {
                  block: "",
                  class: "vc-incoming-action text-none",
                  size: "x-large",
                  onClick: handleAccept
                }, {
                  default: _withCtx(() => [
                    _cache[2] || (_cache[2] = _createTextVNode(" Accept ", -1)),
                    _createVNode(_component_v_icon, {
                      end: "",
                      size: "large"
                    }, {
                      default: _withCtx(() => [...(_cache[1] || (_cache[1] = [
                        _createTextVNode("mdi-check", -1)
                      ]))]),
                      _: 1
                    })
                  ]),
                  _: 1
                }),
                _createVNode(_component_VBtnTextDark, {
                  block: "",
                  class: "vc-incoming-decline text-none",
                  size: "x-large",
                  onClick: handleDecline
                }, {
                  default: _withCtx(() => [
                    _cache[4] || (_cache[4] = _createTextVNode(" Decline ", -1)),
                    _createVNode(_component_v_icon, {
                      end: "",
                      color: "dark",
                      size: "large"
                    }, {
                      default: _withCtx(() => [...(_cache[3] || (_cache[3] = [
                        _createTextVNode("mdi-close", -1)
                      ]))]),
                      _: 1
                    })
                  ]),
                  _: 1
                })
              ])
            ]),
            _: 1
          }, 8, ["class"]), [
            [_vShow, mounted.value]
          ])
        ]),
        _: 1
      })
    ]),
    _: 1
  }))
}
}

})